/**
 * Which sections each field type's properties panel renders, as data (spec R66).
 *
 * This table is the module's **single** answer to that question (ADR-4B). It is
 * read by `selectPropertySections`, which `FieldConfigurationPanel` renders verbatim; no
 * component branches on a field id to add or hide a section. A new field type is
 * one row here, and R66's oracle — open seven panels, compare seven section lists —
 * is one table-driven test.
 *
 * Written out per field id rather than derived from booleans on purpose: the answer
 * a reviewer has to check against the prototype is then literally readable, and the
 * `Record<FieldId, …>` type makes a forgotten field a compile error rather than an
 * empty panel.
 */

import { FieldId, PropertySectionKind } from '../types';

/**
 * The order sections appear in, wherever they appear (spec R66, "in the
 * prototype's order"). Each row below is a subset of this sequence.
 *
 * No field declares more than one of Image display / Description style / Pricing /
 * Button, so their relative order is unobservable; Label first and Custom styling
 * last are the two that are.
 */
export const PROPERTY_SECTION_ORDER: PropertySectionKind[] = [
  'label',
  'alignment',
  'imageFit',
  'descriptionStyle',
  'pricing',
  // Immediately after Pricing, so it renders where the design puts it — directly
  // under "Show unit of measure (UOM)" and above Custom styling. It is a section of
  // its own rather than a second checkbox inside PricingSection because the two have
  // Kept a section of its own even after UOM became per-element too (14021): it lets
  // `savingsPct` offer Strikethrough without declaring Pricing, which would show a
  // unit-of-measure checkbox on a percentage.
  'strikethrough',
  'button',
  'customStyling',
];

/**
 * Field id → its ordered section list.
 *
 * Reading of the v3 prototype (`app.jsx:1924–2018`) as the spec records it:
 * Label for 11 identity/price/availability fields, Alignment for every element
 * with a block, Image display for the image, Description style for the
 * description, Pricing for the four price fields, Button for the CTA, and Custom
 * styling for all 29.
 *
 * Two rows are worth naming:
 * - `image` has no block, so it declares no Alignment (spec R66).
 * - `uom` also renders no block (spec R72) but keeps Alignment, because R72 states
 *   its panel explicitly: "Alignment and Custom styling only". R66's "for every
 *   element that has a block" is the general rule; R72 is the specific one and
 *   wins for this one field.
 */
export const PROPERTY_SECTIONS: Record<FieldId, PropertySectionKind[]> = {
  image: ['imageFit', 'customStyling'],
  sku: ['label', 'alignment', 'customStyling'],
  title: ['label', 'alignment', 'customStyling'],
  brand: ['alignment', 'customStyling'],
  model: ['label', 'alignment', 'customStyling'],
  desc: ['alignment', 'descriptionStyle', 'customStyling'],
  // The five price fields all declare Label and Strikethrough (14021). Label was
  // missing from salePrice and pricePerUnit; adding it is backward-compatible because
  // a blank label renders nothing, so existing cards are unchanged.
  listPrice: ['label', 'alignment', 'pricing', 'strikethrough', 'customStyling'],
  salePrice: ['label', 'alignment', 'pricing', 'strikethrough', 'customStyling'],
  savingsAmt: ['label', 'alignment', 'pricing', 'strikethrough', 'customStyling'],
  savingsPct: ['label', 'alignment', 'strikethrough', 'customStyling'],
  pricePerUnit: ['label', 'alignment', 'pricing', 'strikethrough', 'customStyling'],
  currency: ['label', 'alignment', 'customStyling'],
  stock: ['alignment', 'customStyling'],
  leadTime: ['label', 'alignment', 'customStyling'],
  qtyAvail: ['label', 'alignment', 'customStyling'],
  discontinued: ['alignment', 'customStyling'],
  starRating: ['alignment', 'customStyling'],
  reviewCount: ['alignment', 'customStyling'],
  moq: ['label', 'alignment', 'customStyling'],
  qtyIncrement: ['label', 'alignment', 'customStyling'],
  preferred: ['alignment', 'customStyling'],
  badge: ['alignment', 'customStyling'],
  promoMsg: ['alignment', 'customStyling'],
  newArrival: ['alignment', 'customStyling'],
  bestSeller: ['alignment', 'customStyling'],
  sponsored: ['alignment', 'customStyling'],
  cta: ['alignment', 'button', 'customStyling'],
  qtySelector: ['alignment', 'customStyling'],
};
